import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneRight, ChatCircleDots, Sparkle } from "@phosphor-icons/react";
import { Disclaimer } from "@/components/Disclaimer";
import api, { API } from "@/lib/api";

const STARTERS = [
  "Why do I keep repeating the same pattern?",
  "Help me understand my core wound.",
  "What's one practice I can do today?",
  "How does my archetype show up in relationships?",
];

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [nudge, setNudge] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/coach/history").then(({ data }) => setMessages(data.messages || [])).finally(() => setLoaded(true));
    api.get("/coach/nudge").then(({ data }) => setNudge(data)).catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/coach/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        if (copy[copy.length - 1]?.role === "assistant" && !copy[copy.length - 1].content) {
          copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble responding right now. Please try again." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col lg:h-[calc(100vh-4rem)]">
      <div className="shrink-0">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">AI Coach</p>
        <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Your depth companion</h1>
      </div>

      {nudge && nudge.kind !== "empty" && messages.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 shrink-0 rounded-2xl border border-teal-500/30 bg-teal-500/5 p-4" data-testid="coach-nudge">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-500/15 text-teal-400"><Sparkle size={16} weight="duotone" /></span>
            <div className="flex-1">
              <p className="text-sm leading-relaxed text-slate-200">{nudge.nudge}</p>
              <button onClick={() => send(nudge.nudge)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-300 hover:text-teal-200" data-testid="coach-nudge-cta">{nudge.cta} →</button>
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-5 flex-1 overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="coach-messages">
        {loaded && messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-500/10 text-teal-400"><ChatCircleDots size={28} weight="duotone" /></span>
            <h2 className="mt-4 font-heading text-xl font-medium text-slate-100">I know your patterns. Let&apos;s go deep.</h2>
            <p className="mt-2 max-w-sm text-sm text-slate-500">I&apos;m grounded in your profile, archetype, and recent journals. Ask me anything.</p>
            <div className="mt-6 grid w-full max-w-lg gap-2 sm:grid-cols-2">
              {STARTERS.map((s) => (
                <button key={s} onClick={() => send(s)} className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-left text-sm text-slate-300 transition-all hover:border-teal-500/40 hover:text-teal-200" data-testid="coach-starter">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-5">
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-teal-500 text-slate-950" : "border border-slate-800 bg-slate-900 text-slate-200"
              }`} data-testid={`coach-msg-${m.role}`}>
                {m.role === "assistant" && !m.content && streaming ? (
                  <span className="flex gap-1"><Dot /><Dot d={0.2} /><Dot d={0.4} /></span>
                ) : (
                  <span className="whitespace-pre-wrap">{m.content}</span>
                )}
              </div>
            </motion.div>
          ))}
          <div ref={endRef} />
        </div>
      </div>

      <div className="mt-4 shrink-0">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-3">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask your coach…"
            className="flex-1 rounded-full border border-slate-800 bg-slate-950 px-5 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
            data-testid="coach-input"
          />
          <button type="submit" disabled={streaming || !input.trim()} className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-500 text-slate-950 transition-all hover:bg-teal-400 disabled:opacity-40" data-testid="coach-send-btn">
            <PaperPlaneRight size={18} weight="fill" />
          </button>
        </form>
        <div className="mt-3">
          <Disclaimer compact />
        </div>
      </div>
    </div>
  );
}

function Dot({ d = 0 }) {
  return <motion.span className="h-2 w-2 rounded-full bg-slate-500" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d }} />;
}
